import ProfileWrap from "@/features/profile/layouts/ProfileWrap";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ui } from "@patient-watch/ui";
import { Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabaseClient as supabase } from "@/lib/supabase";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { ComboBox, Input } from "@patient-watch/forms";
import { SPECIALTIES } from "@patient-watch/types";
import { getSpecialtyLabel } from "@/features/i18n/clinicalTermLabels";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { resolvePinnedClinicianSpecialty } from "@/features/onboarding/utils/clinicianSpecialty";
import { getClinicianPostProfessionalDestination } from "@/features/onboarding/utils/getClinicianPostProfessionalDestination";
import { useProfileQuery } from "@/features/profile/api/selectProfile";
import { useAuth } from "@/utils/auth";
import { useEffect, useMemo, useState } from "react";

const formSchema = z.object({
    clinician_number: z.string().optional(),
    specialty: z.string().min(2, "Please select or enter your specialty"),
});

type FormSchemaType = z.infer<typeof formSchema>;

const useUpdateProfessionalDetailsMutation = () => {
    const toastCopy = useToastCopy();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["update-professional-details"],
        mutationFn: async ({ clinician_number, specialty }: FormSchemaType) => {
            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                throw new Error("Not authenticated");
            }

            // Update clinician record
            const { error } = await supabase
                .from("clinicians")
                .update({
                    clinician_number: clinician_number || null,
                    specialty: specialty,
                })
                .eq("clinician_id", user.id);

            if (error) {
                throw new Error(error.message || "Failed to update professional details");
            }

            return { success: true };
        },
        onError: (error) => {
            console.error("Update professional details error:", error);
            toast.error(error.message || toastCopy.failedUpdateProfessionalDetails);
        },
        onSuccess: async () => {
            toast.success(toastCopy.professionalDetailsSaved);
            queryClient.invalidateQueries({ queryKey: ["profile"] });

            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                navigate({ to: "/templates/public" });
                return;
            }

            const destination = await getClinicianPostProfessionalDestination(user.id);
            navigate({ to: destination });
        },
    });
};

const ClinicianProfessional = () => {
    const mutation = useUpdateProfessionalDetailsMutation();
    const copy = useProfileCopy();
    const { locale } = useAppLocale();
    const auth = useAuth();
    const navigate = useNavigate();
    const { data: profile } = useProfileQuery();
    const [isSkippingPinnedSpecialty, setIsSkippingPinnedSpecialty] = useState(false);

    const { data: clinicianRow, isFetched: isClinicianFetched } = useQuery({
        queryKey: ["clinician-professional", auth.id],
        enabled: Boolean(auth.id),
        queryFn: async () => {
            const { data, error } = await supabase
                .from("clinicians")
                .select("specialty, clinician_number")
                .eq("clinician_id", auth.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
    });

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            clinician_number: "",
            specialty: "",
        },
    });

    const pinnedSpecialty = resolvePinnedClinicianSpecialty({
        clinicianSpecialty: clinicianRow?.specialty,
        userMetaData: profile?.user_meta_data,
    });

    useEffect(() => {
        const nextNumber = clinicianRow?.clinician_number?.trim() || "";
        if (!pinnedSpecialty && !nextNumber) return;
        form.reset({
            specialty: pinnedSpecialty,
            clinician_number: nextNumber,
        });
    }, [clinicianRow?.clinician_number, form, pinnedSpecialty]);

    // Study invites can pin specialty - skip specialty + registration number entirely.
    useEffect(() => {
        if (!auth.id || !isClinicianFetched || !pinnedSpecialty || isSkippingPinnedSpecialty) return;

        setIsSkippingPinnedSpecialty(true);
        void (async () => {
            const destination = await getClinicianPostProfessionalDestination(auth.id);
            void navigate({ to: destination });
        })();
    }, [auth.id, isClinicianFetched, isSkippingPinnedSpecialty, navigate, pinnedSpecialty]);

    // Stored value stays English; labels follow the clinician's app locale.
    const specialtyOptions = useMemo(
        () =>
            SPECIALTIES.map((specialty) => ({
                label: getSpecialtyLabel(specialty, locale),
                value: specialty,
            })).toSorted((a, b) => a.label.localeCompare(b.label, locale)),
        [locale]
    );

    const onSubmit = async (formData: FormSchemaType) => {
        await mutation.mutateAsync(formData);
    };

    if (!isClinicianFetched || pinnedSpecialty || isSkippingPinnedSpecialty) {
        return (
            <ProfileWrap title={copy.professional}>
                <div className="flex justify-center items-center py-12" data-testid="professional-skipping">
                    <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                </div>
            </ProfileWrap>
        );
    }

    return (
        <ProfileWrap title={copy.professional}>
            <ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-testid="professional-form">
                    <div data-testid="specialty-field">
                        <ComboBox
                            name="specialty"
                            label={copy.medicalSpecialty}
                            options={specialtyOptions}
                            placeholder={copy.selectSpecialty}
                            description={copy.specialtyDescription}
                            autoFocus
                        />
                    </div>

                    <Input
                        name="clinician_number"
                        label={copy.professionalRegistrationNumber}
                        placeholder={copy.professionalRegistrationPlaceholder}
                        description={copy.professionalRegistrationDescription}
                    />

                    <ui.Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={mutation.isPending}
                        data-testid="continue-button"
                    >
                        {mutation.isPending && <Loader2 className="mr-2 w-4 h-4 animate-spin" />}
                        {mutation.isPending ? copy.saving : copy.continue}
                    </ui.Button>
                </form>
            </ui.Form>
        </ProfileWrap>
    );
};

export const Route = createFileRoute("/onboarding/clinician/professional")({
    component: ClinicianProfessional,
});
